import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, map, Observable, Subject, take } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '@src/environments/environment';
import { Logger } from '@core/utils/logger';

export interface WebSocketMessage {
  event: string;
  category: string;
  roomId: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface ConnectionStatus {
  connected: boolean;
  connecting: boolean;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class WebSocketService implements OnDestroy {
  logger = new Logger('WebSocketService');

  private socket?: WebSocket;
  private destroy$ = new Subject<void>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private heartbeatTimer?: ReturnType<typeof setTimeout>;
  private readonly heartbeatInterval = 5 * 60 * 1000; // 5 minutes
  private currentUserId?: string;

  // Observables
  private connectionStatus$ = new BehaviorSubject<ConnectionStatus>({
    connected: false,
    connecting: false,
  });
  private messages$ = new Subject<WebSocketMessage>();

  /**
   * Connect to WebSocket with userId
   */
  public connect(userId: string, token: string): Observable<ConnectionStatus> {
    if (this.socket?.readyState === WebSocket.OPEN) {
      return this.connectionStatus$.asObservable();
    }

    this.currentUserId = userId;
    this.connectionStatus$.next({ connected: false, connecting: true });

    try {
      this.socket = new WebSocket(`${environment.socketHost}?userId=${userId}&token=${token}`);
      this.setupEventListeners();
    } catch (error) {
      this.logger.error('WebSocket connection error:', error);
      this.connectionStatus$.next({
        connected: false,
        connecting: false,
        error: 'Failed to connect',
      });
    }

    return this.connectionStatus$.asObservable();
  }

  /**
   * Disconnect from WebSocket
   */
  public disconnect(): void {
    this.clearReconnectTimer();
    this.clearHeartbeatTimer();
    this.reconnectAttempts = 0;

    if (this.socket) {
      this.socket.close(1000, 'Manual disconnect');
      this.socket = undefined;
      this.logger.info('Disconnected');
    }

    this.connectionStatus$.next({ connected: false, connecting: false });
    this.currentUserId = undefined;
  }

  /**
   * Join a room
   */
  public joinRoom(roomId: string): void {
    if (!this.isConnected()) {
      this.logger.warn('Cannot join room: WebSocket not connected');
      return;
    }

    this.logger.info(`Joining room: ${roomId}`);

    this.sendMessage({
      action: 'join',
      roomId,
      userId: this.currentUserId,
    });
  }

  /**
   * Leave a room
   */
  public leaveRoom(roomId: string): void {
    if (!this.isConnected()) {
      this.logger.warn('Cannot leave room: WebSocket not connected');
      return;
    }

    this.logger.info(`Leaving room: ${roomId}`);

    this.sendMessage({
      action: 'leave',
      roomId,
    });
  }

  /**
   * Get connection status observable
   */
  public getConnectionStatus(): Observable<ConnectionStatus> {
    return this.connectionStatus$.asObservable();
  }

  public waitForConnection(): Observable<void> {
    return this.connectionStatus$.pipe(
      filter(status => status.connected),
      take(1),
      map(() => void 0),
    );
  }

  /**
   * Get messages observable
   */
  public getMessages(): Observable<WebSocketMessage> {
    return this.messages$.asObservable();
  }

  /**
   * Check if currently connected
   */
  public isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection opened
    this.socket.onopen = (): void => {
      this.logger.info('WebSocket connected');
      this.connectionStatus$.next({ connected: true, connecting: false });
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;

      // Start heartbeat
      this.startHeartbeat();
    };

    // Message received
    this.socket.onmessage = (event): void => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.messages$.next(message);
        this.logger.info('Message received:', message);
      } catch (error) {
        this.logger.error('Error parsing WebSocket message:', error);
      }
    };

    // Connection closed
    this.socket.onclose = (event): void => {
      this.logger.info('WebSocket connection closed');
      this.connectionStatus$.next({ connected: false, connecting: false });
      this.clearHeartbeatTimer();

      // Attempt to reconnect if it wasn't a manual disconnect
      if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
        this.attemptReconnect();
      }
    };

    // Connection error
    this.socket.onerror = (error): void => {
      this.logger.error('WebSocket error:', error);
      this.connectionStatus$.next({
        connected: false,
        connecting: false,
        error: 'Connection error',
      });
    };
  }

  private sendMessage(message: { action: string, roomId?: string, userId?: string }): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      this.logger.warn('Cannot send message: WebSocket not connected');
    }
  }

  private attemptReconnect(): void {
    this.reconnectAttempts++;

    this.logger.info(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    this.connectionStatus$.next({
      connected: false,
      connecting: true,
      error: `Reconnecting... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`,
    });

    this.reconnectTimer = setTimeout(() => {
      if (this.socket && this.currentUserId) {
        // Store the current URL to reconnect to the same endpoint
        const currentUrl = this.socket.url;
        this.socket = new WebSocket(currentUrl);
        this.setupEventListeners();
      }
    }, this.reconnectDelay);

    // Exponential backoff with jitter
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000) + Math.random() * 1000;
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }

  private startHeartbeat(): void {
    this.clearHeartbeatTimer();
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected()) {
        this.sendMessage({ action: 'ping' });
      }
    }, this.heartbeatInterval);
  }

  private clearHeartbeatTimer(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnect();
  }
}
